import * as React from 'react';

import type WorkOrderModalViewModel from 'ab-viewModels/scheduleBoardWorkOrderModal.viewModel';

import * as FormattingUtils from '@acceligentllc/shared/utils/formatting';
import { calculateManHourAverage, noRevenue } from '@acceligentllc/shared/utils/productionData';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { SegmentLabel } from '@acceligentllc/storybook';

import LockedValue from 'af-components/LockedValue';

import Changes from '../Changes';

interface OwnProps {
	workOrderDataToShow: WorkOrderModalViewModel;
	currentWorkOrderData: WorkOrderModalViewModel;
	isRevisionOpened: boolean;
}

const automaticCalculationAfter = new Date(process.env.AUTO_PROD_DATA_CUTOFF_DATE ?? '');

type Props = OwnProps;

const ProductionDataSection: React.FC<Props> = (props) => {

	const { workOrderDataToShow, currentWorkOrderData, isRevisionOpened } = props;

	const isInternal = workOrderDataToShow?.isJobInternal;
	const _sectionHasChanges = sectionHasChanges();
	const hasOnlyNonBillableWork = !isRevisionOpened
		&& shouldUseAutomaticCalculations(currentWorkOrderData)
		&& currentWorkOrderData.hasOnlyNonBillableWork;

	const revenueSubtractions = (process.env.FTD_DISABLE_PR_DIFF !== 'true' ? currentWorkOrderData.payrollDifferential ?? 0 : 0)
		+ +(currentWorkOrderData.materialCost ?? 0)
		- +(currentWorkOrderData.salesMarkup ?? 0);

	const serviceRevenueSubtractions = (process.env.FTD_DISABLE_PR_DIFF !== 'true' ? currentWorkOrderData.payrollDifferential ?? 0 : 0) + +(currentWorkOrderData.materialCost ?? 0);

	const serviceRevenue = React.useMemo(() => {
		if (currentWorkOrderData.revenue && currentWorkOrderData.salesMarkup) {
			return +currentWorkOrderData.revenue - +currentWorkOrderData.salesMarkup;
		} else if (currentWorkOrderData.revenue) {
			return currentWorkOrderData.revenue;
		}
		return null;
	}, [currentWorkOrderData.revenue, currentWorkOrderData.salesMarkup]);

	return (
		!isInternal &&
		<>
			<hr />
			<div className="work-order-info-modal__production-data-container">
				<Changes modifiedData={_sectionHasChanges} />
				<SegmentLabel
					label="Production data"
					tooltip="Bottom line information on revenues, costs, worked times and such."
				/>
				<div className="work-order-info-modal__production-data">
					<div>
						<LockedValue
							label="Revenue"
							value={hasOnlyNonBillableWork
								? noRevenue({
									dueDate: TimeUtils.formatDate(currentWorkOrderData.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
									hasOnlyNonBillableWork,
									revenueSubtractions,
								})
								: FormattingUtils.moneyNormalizer(currentWorkOrderData.revenue)}
						/>
					</div>
					<div>
						<LockedValue
							label="Service Revenue"
							value={hasOnlyNonBillableWork
								? noRevenue({
									dueDate: TimeUtils.formatDate(currentWorkOrderData.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
									hasOnlyNonBillableWork,
									revenueSubtractions: serviceRevenueSubtractions,
								})
								: FormattingUtils.moneyNormalizer(serviceRevenue)}
						/>
					</div>
					<div>
						<LockedValue
							label="Man Hour Average"
							value={manHourAverageValue(currentWorkOrderData)}
						/>
					</div>
					{
						process.env.FTD_DISABLE_PR_DIFF !== 'true' &&
						currentWorkOrderData.payrollDifferential !== null &&
						<div>
							<LockedValue
								label="P/R Diff"
								value={FormattingUtils.moneyNormalizer(currentWorkOrderData.payrollDifferential)}
							/>
						</div>
					}

				</div>
				<div className="work-order-info-modal__production-data">
					<div>
						<LockedValue
							label="Job Hours"
							value={currentWorkOrderData?.jobHours ?? '-'}
						/>
					</div>
					<div>
						<LockedValue
							label="Shop Hours"
							value={currentWorkOrderData?.shopHours ?? '-'}
						/>
					</div>
					<div>
						<LockedValue
							label="Travel Hours"
							value={currentWorkOrderData?.travelHours ?? '-'}
						/>
					</div>

				</div>
			</div>
		</>
	);

	function sectionHasChanges() {
		return workOrderDataToShow?.revenue !== currentWorkOrderData?.revenue
			|| workOrderDataToShow?.manHourAverage !== currentWorkOrderData?.manHourAverage
			|| workOrderDataToShow?.jobHours !== currentWorkOrderData?.jobHours
			|| workOrderDataToShow?.shopHours !== currentWorkOrderData?.shopHours
			|| workOrderDataToShow?.travelHours !== currentWorkOrderData?.travelHours
			|| workOrderDataToShow?.workDescription !== currentWorkOrderData?.workDescription;
	}

	function shouldUseAutomaticCalculations(workOrderData: WorkOrderModalViewModel) {
		const _dueDate: Date = new Date(TimeUtils.formatDate(workOrderData.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY));

		const areDatesValid = !(isNaN(automaticCalculationAfter.getTime()) || isNaN(_dueDate.getTime()));

		if (!areDatesValid) {
			throw new Error('Dates for determining production data display are invalid.');
		}

		if (automaticCalculationAfter >= _dueDate) {
			return false;
		}

		return true;
	}

	function manHourAverageValue(workOrderData: WorkOrderModalViewModel) {

		if (workOrderData.revenue === null) {
			return 'N/A';
		}

		if (shouldUseAutomaticCalculations(workOrderData)) {
			return FormattingUtils.moneyNormalizer(workOrderData.manHourAverage);
		}

		const manHourAverage = calculateManHourAverage(
			workOrderData.revenue !== null ? Number(workOrderData.revenue) : null,
			workOrderData.jobHours ?? 0,
			workOrderData.travelHours ?? 0,
			workOrderData.shopHours ?? 0
		);

		if (manHourAverage === null) {
			return 'N/A';
		}

		return FormattingUtils.moneyNormalizer(manHourAverage);
	}

};

export default ProductionDataSection;
